/**
 * MyWorkView (P268.2) -- the unified "my work" inbox.
 *
 * Route: /my-work
 *
 * One ranked list of the CAPs / framework assessments / attestations /
 * governance ceremonies awaiting the current user, grouped by entity
 * type, overdue-first, each deep-linking into its existing detail
 * surface. Any logged-in user sees their own work.
 *
 * The list is split on `ownership`. An item is "mine" when the viewer
 * is the named signatory or assignee; it is "queue" when they can
 * reach it only through a shared queue or an admin role. Mixing the
 * two -- which the CX walkthrough found -- means a Service Owner opens
 * an inbox of the CISO's CAPs and cannot tell which signature is
 * actually wanted from him, so queue items are collapsed beneath.
 */
import { itemTitle } from "shared/work/itemTitle";
import { roleBadge, stateLabel } from "shared/vocab/workflowState";
import { Figure } from "shared/components/Figure";
import { Link, useSearchParams } from "react-router-dom";

import { refusalKey } from "shared/api/refusal";
import { refusalMessage } from "shared/api/refusalMessage";
import { ApiError } from "shared/api/client";
import { roleLabel } from "shared/vocab/workflowState";
import { ReviewAndSign } from "shared/components/ReviewAndSign";
import { Explainer, InfoTip } from "shared/components/Explainer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "shared/time/formatDate";

import { api } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";
import type { WorkAction, WorkItem } from "shared/api/types";
import { useMyWork, usePerformWorkAction } from "shared/work/useMyWork";
import { BAND_ORDER, groupByDue, type DueBand } from "shared/work/dueBands";

const ROLE_TONE: Record<string, string> = {
  preparer: "bg-sky-100 text-sky-800",
  reviewer: "bg-indigo-100 text-indigo-800",
  approver: "bg-emerald-100 text-emerald-800",
  submitter: "bg-sky-100 text-sky-800",
  // P273.4: the ceremony sign-off roles.
  cro: "bg-amber-100 text-amber-900",
  business_owner: "bg-amber-100 text-amber-900",
  ae: "bg-amber-100 text-amber-900",
  service_owner: "bg-amber-100 text-amber-900",
  challenge: "bg-rose-100 text-rose-800",
};

function isOverdue(due: string | null): boolean {
  return due !== null && due < new Date().toISOString().slice(0, 10);
}

/**
 * F2 -- the subtitle names the construct the row is about, and for a
 * ceremony that construct is met cold. The appendix asked for this on
 * a "GOVERNANCE SIGN-OFFS" heading; C4 regrouped the inbox by due
 * date, so the row is where it lands now.
 *
 * Keyed on the subtitle the backend composes. An unmapped subtitle
 * gets no tip, which is the right failure: silence beats a definition
 * of the wrong thing.
 */
const ROW_TERM: Record<string, string> = {
  "IBS admission": "admission",
  "MVB pack": "mvb",
  "MVB declaration": "mvb",
  "Service classification": "service_classification",
  "Resilience attestation": "drra",
  /* Round 12 H2 -- this pointed at `finding`, so the four plan rows on
     this page explained what a Finding is to the persona whose whole
     job runs through plans. The glossary had no entry for a corrective
     action plan, which is how it ended up borrowing one. */
  "Corrective action plan": "cap",
};

/** The work item a notification sent the reader to, if any. */
/** Round 18 N10 -- the seat that performs a step: a challenge is the
 *  reviewer's, named as the reviewer ("Rashid Reviewer (Reviewer)"). */
function seatOf(step: string): string {
  return step === "challenge" ? "reviewer" : step;
}

/** Round 18 N10 -- who an awaited leg is with. An owner's leg is the
 *  record's owner; any other leg is the one person holding its seat.
 *  The challenge rows named the service's owner on a reviewer's leg. */
function awaitedPerson(item: WorkItem): string | null | undefined {
  return item.my_role === "business_owner" || item.my_role === "service_owner"
    ? item.owner_name
    : item.awaiting_name;
}

function useFocusedWorkItem(): string | null {
  const [params] = useSearchParams();
  return params.get("focus");
}

function WorkRow({
  item,
  onPanel = () => {},
}: {
  item: WorkItem;
  onPanel?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const overdue = isOverdue(item.due_date);
  /* ROUND 13 H, third round -- the bell "links nowhere".
     
     It does link: a ceremony ask lands here, because this is where the
     signature is actually taken and the admin console would turn the
     addressee away at the door (round 12 A6). But it landed on the
     INBOX, and the notification that sent him named a record -- so the
     man who clicked "Service classification awaiting CRO sign-off:
     Core Banking T24" arrived at a list and had to find it again.
     
     The ask carries the row it is about; the page scrolls to it and
     rings it. */
  const focused = useFocusedWorkItem() === item.id;
  const ref = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: "center" });
  }, [focused]);
  return (
    <li ref={ref}>
      <Link
        to={item.deep_link}
        data-testid={`work-item-${item.id}`}
        data-focused={focused ? "true" : undefined}
        className={`flex items-center gap-3 rounded border bg-white px-3 py-2 hover:border-brand-brass ${
          focused ? "border-brand-brass ring-2 ring-brand-brass/40" : "border-slate-200"
        }`}
      >
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            ROLE_TONE[item.my_role] ?? "bg-slate-100 text-slate-700"
          }`}
        >
          {roleBadge(item.my_role)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-800">
            {itemTitle(t, item)}
          </span>
          <span className="block text-xs text-slate-500">
            {item.subtitle_key
              ? t(item.subtitle_key, {
                  ...(item.subtitle_params ?? {}),
                  defaultValue: item.subtitle,
                })
              : item.subtitle}
            {ROW_TERM[item.subtitle] && (
              <InfoTip termKey={ROW_TERM[item.subtitle] as string} />
            )}{" "}
            {/* D4 -- a sentence stitched from a subtitle, a middle dot
                and a state. Reordered, the state moved to the far side
                of the row and the dot with it. `dir="auto"` on the
                state takes its direction from its own first character,
                so an English seat name stays intact inside an Arabic
                line without claiming the line is English. */}
            · <span dir="auto">{stateLabel(item.workflow_state)}</span>
          </span>
        </span>
        {/* B2 -- a ceremony has no deadline, so the row said nothing
            about time at all and 24 of them looked simultaneous. When
            it was RAISED is a fact the record holds. Rendered as what
            it is, never as a due date. */}
        {!item.due_date && item.raised_at && (
          <span className="shrink-0 text-xs text-slate-400">
            {t("myWork.raisedOn", { date: item.raised_at.slice(0, 10) })}
          </span>
        )}
        {item.due_date && (
          <span
            data-testid="my-work-due"
            className={`shrink-0 text-xs ${overdue ? "font-semibold text-rose-700" : "text-slate-400"}`}
          >
            {overdue ? t("screens.myWorkView.overdue") : t("screens.myWorkView.due")}
            {/* D2/D4 -- a date stitched onto a translated word rather
                than interpolated into it. Reordered, it rendered
                "Due2026-09-29" with the date on the wrong side.
                ROUND 15 H3 -- and with a margin between them the TEXT
                still read "overdue2026-09-15"; a space is a character. */}{" "}
            <Figure>{item.due_date}</Figure>
          </span>
        )}
      </Link>
      {item.action ? (
        <ActOnItem item={item} action={item.action} onPanel={onPanel} />
      ) : (
        item.ownership === "queue" && <QueueRowCaption item={item} />
      )}
    </li>
  );
}

/**
 * Round 10 F5 -- one act of judgement over a business service.
 *
 * Omar's Thursday is twenty pack rows across thirteen headings, twenty
 * "Review and sign" at three clicks each, against the fifteen minutes
 * his persona card allows. Gregor's call (2026-09-14): a group-level
 * "no objection" is allowed, and every row it produces must say it was
 * given as part of one.
 *
 * It is NOT a shortcut past the panel. The same review panel opens
 * first, listing what the one signature covers -- B1 put that panel
 * there because the walkthrough found signers who could not say what
 * their signature committed the bank to, and collapsing it to save a
 * click would undo a governance fix to save a click. What it removes
 * is the repetition, not the reading.
 */
function GroupNoObjection({
  subject,
  rows,
  onPanel,
}: {
  subject: string;
  rows: WorkItem[];
  onPanel: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  // Only the rows this one signature would actually cover: MVB packs
  // in this group, awaiting the same seat, that are the viewer's to
  // sign. Anything else in the heading is left alone.
  const role = rows[0]?.my_role ?? "";
  const covered = rows.filter(
    (w) =>
      w.entity_type === "ceremony" &&
      w.subtitle_key?.startsWith("myWork.rowSubtitle.mvbPack") &&
      w.ownership !== "queue" &&
      w.my_role === role,
  );
  /* Round 14 G, eighth round -- and the step before the signatures.
     Omar's queue was twenty-three CHALLENGE rows with no group act; the
     group decision covered only the signatures that come after his. */
  const isChallenge = role === "challenge";
  const signable =
    (role === "cro" || role === "ae" || isChallenge) && covered.length > 1;
  // ROUND 15 C12 -- the group act takes a comment where every other
  // signature does; given once, recorded on each pack.
  const [groupNote, setGroupNote] = useState("");

  const sign = useMutation({
    mutationFn: () => {
      const note = groupNote.trim();
      return isChallenge
        ? api
            .post<{ challenged: number }>("/v1/admin/twin/mvb/packs/group-challenge", {
              group_subject: subject,
              pack_ids: covered.map((w) => w.id),
              ...(note ? { note } : {}),
            })
            .then((r) => ({ signed: r.challenged, approved: 0 }))
        : api.post<{ signed: number; approved: number }>(
            "/v1/admin/twin/mvb/packs/group-signoff",
            { role, group_subject: subject, ...(note ? { note } : {}) },
          );
    },
    onSuccess: (r) => {
      setOpen(false);
      onPanel(false);
      toast.push({
        tone: "success",
        message: isChallenge
          ? t("myWork.groupChallenge.done", { count: r.signed, subject })
          : t("myWork.groupSignoff.done", { count: r.signed, subject }),
      });
      void qc.invalidateQueries({ queryKey: ["my-work"] });
    },
    onError: (e: { message?: string }) =>
      toast.push({
        tone: "error",
        message: e.message ?? t("myWork.groupSignoff.failed"),
      }),
  });

  if (!signable) return null;
  const name = me.data?.display_name ?? me.data?.username ?? "";

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`my-work-noobjection-${subject}`}
        onClick={() => {
          setOpen(true);
          onPanel(true);
        }}
        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-[11px] text-brand-brassDark hover:bg-brand-brass/10"
      >
        {isChallenge
          ? t("myWork.groupChallenge.review", { count: covered.length })
          : t("myWork.groupSignoff.review", { count: covered.length })}
      </button>
    );
  }
  return (
    <ReviewAndSign
      testid={`group-${subject}`}
      title={
        isChallenge
          ? t("myWork.groupChallenge.title", { subject })
          : t("myWork.groupSignoff.title", { subject })
      }
      summary={covered.map((w) => ({
        label_key: "myWork.groupSignoff.member",
        value: w.title,
      }))}
      commitsToKey={
        isChallenge ? "myWork.groupChallenge.commits" : "myWork.groupSignoff.commits"
      }
      recordLink="/twin/mvb-candidates"
      pending={sign.isPending}
      confirmLabel={
        sign.isPending
          ? t("myWork.action.working")
          : isChallenge
            ? t("myWork.groupChallenge.confirm", { count: covered.length, name })
            : t("myWork.groupSignoff.confirm", { count: covered.length, name })
      }
      onCancel={() => {
        setOpen(false);
        onPanel(false);
      }}
      onConfirm={() => sign.mutate()}
    >
      <label className="block text-xs text-slate-600">
        {t("myWork.groupAct.comment")}
        <textarea
          data-testid={`work-sign-comment-group-${subject}`}
          value={groupNote}
          onChange={(e) => setGroupNote(e.target.value)}
          rows={2}
          className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm"
        />
      </label>
    </ReviewAndSign>
  );
}

/**
 * A1 -- who signs this, on a row the viewer cannot.
 *
 * The band used to carry a live "Review and sign" on legs belonging to
 * somebody else; the panel opened under the reader's name and the
 * server refused. Dropping the button is only half the fix -- a row
 * with nothing on it says "reachable" where the reader needs "waiting
 * on Khalid, and he does it from his own inbox".
 *
 * Names the person where the record has one, the seat where it does
 * not. A record with no owner recorded says so rather than inventing
 * one, because an unowned ceremony is a gap for an overseer to notice.
 */
function QueueRowCaption({ item }: { item: WorkItem }) {
  const { t } = useTranslation();
  // B3 -- the regression. This caption was written for work belonging
  // to somebody else and was rendered on every queue row, so a
  // preparer's own cycle read "Awaiting the Preparer; they sign from My
  // Work" inside a band headed "In your queue, not yours". It told
  // people to wait for themselves.
  //
  // Some queue work genuinely IS the viewer's: an assessment cycle
  // belongs to the whole framework population rather than to one
  // person, which is why it is filed as a queue item and why saying
  // "awaiting somebody else" about it is false. The server is the only
  // place that knows both the awaiting seat and the viewer's seats, so
  // it tells us which case this is.
  return (
    <p
      data-testid={`work-awaiting-${item.id}`}
      className="mt-1 ps-3 text-xs text-slate-500"
    >
      {item.awaiting_you
        ? t("myWork.awaiting.shared", { role: roleBadge(item.my_role) })
        : awaitedPerson(item)
          ? t("myWork.awaiting.named", {
              name: awaitedPerson(item),
              role: roleBadge(seatOf(item.my_role)),
            })
          : item.my_role === "challenge"
            ? // Round 17 H37 -- a challenge is not a seat ("Awaiting the
              // Challenge"); the reviewer records it.
              t("myWork.awaiting.challenge")
            : /* Round 19 G42 -- a bare "Awaiting the Business owner"
                 stood where no owner was recorded and nobody could
                 sign. Said, where the server knows nobody holds it. */
              item.awaiting_holders === 0
              ? item.my_role === "business_owner" || item.my_role === "service_owner"
                ? t("myWork.awaiting.noOwner", { role: roleBadge(item.my_role) })
                : t("myWork.awaiting.nobody", { role: roleBadge(item.my_role) })
              : t("myWork.awaiting.seat", { role: roleBadge(item.my_role) })}
    </p>
  );
}

/**
 * The act-here affordance on a ceremony row.
 *
 * The signature is the SESSION's, not typed: an inbox that asks a human
 * to key in a name is the same hole the sign-off gates just closed, one
 * layer up. If the session carries no display name or email we say so
 * and refuse, rather than signing a sealed ceremony as "unknown".
 */
function ActOnItem({
  item,
  action,
  onPanel,
}: {
  item: WorkItem;
  action: WorkAction;
  onPanel: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const toast = useToast();
  const perform = usePerformWorkAction();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const name = me.data?.display_name ?? me.data?.username ?? "";
  const email = me.data?.email ?? "";
  const signable =
    action.signature_fields.length === 0 || (name !== "" && email !== "");

  if (perform.isSuccess) {
    return (
      <p
        data-testid={`work-action-done-${item.id}`}
        className="mt-1 ps-3 text-xs text-emerald-700"
      >
        {t("myWork.action.done")}
      </p>
    );
  }

  // B1 -- the button used to sign on the first click, with no summary
  // and no confirmation, and the list reordered under the cursor as
  // others acted. Two people signed the wrong thing. It now opens the
  // decision first: what is being signed, the challenge if one was
  // recorded, what it commits the bank to, and only then a signature
  // carrying the signer's own name.
  if (!open) {
    return (
      <div className="mt-1 flex items-center gap-2 ps-3">
        <button
          type="button"
          disabled={!signable}
          data-testid={`work-action-${item.id}`}
          onClick={() => {
            setOpen(true);
            onPanel(true);
          }}
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {/* Round 17 G20 -- a submitter confirms and submits; he does
              not sign. "Review and sign" sat on attestations where his
              only seat was SUBMITTER. */}
          {action.body?.["action"] === "submit"
            ? t("myWork.action.reviewSubmit")
            : t("myWork.action.review")}
        </button>
        {!signable && (
          <span
            data-testid={`work-action-unsignable-${item.id}`}
            className="text-xs text-amber-800"
          >
            {t("myWork.action.noIdentity")}
          </span>
        )}
      </div>
    );
  }

  return (
    <ReviewAndSign
      testid={item.id}
      title={itemTitle(t, item)}
      ownerName={item.owner_name}
      summary={item.summary ?? []}
      signedSoFar={item.signed_so_far ?? []}
      commitsToKey={item.commits_to_key}
      challengeNote={item.challenge_note}
      recordLink={item.deep_link}
      pending={perform.isPending}
      confirmLabel={
        perform.isPending
          ? t("myWork.action.working")
          : // D7 + D8 -- it read "Sign as Record second-line challenge":
            // the action's own verb wrapped in a sentence about
            // signing, which both garbled it and called a challenge a
            // signature. A challenge keeps its verb. A signature names
            // the PERSON signing, which is the whole point of a
            // signature and what the confirm never said.
            action.is_signature === false
            ? action.label
            : name
              ? t("myWork.sign.signAs", { name })
              : // Round 9 G -- "Sign as Head of Operational
                // Resilience" was composed in the backend, so it
                // stayed English under any translation. The seat is
                // a VALUE the reader's catalogue names.
                action.label_key
                ? t(action.label_key, {
                    ...(action.label_params ?? {}),
                    ...(action.label_params?.role
                      ? { role: roleLabel(action.label_params.role) }
                      : {}),
                  })
                : action.label
      }
      onCancel={() => {
        setOpen(false);
        onPanel(false);
      }}
      onConfirm={() =>
        perform.mutate(
          {
            action,
            name,
            email,
            ...(comment.trim() ? { note: comment.trim() } : {}),
          },
          {
            onSuccess: () =>
              toast.push({
                tone: action.next_owner_missing
                  ? "warning"
                  : action.next_role_unnotified
                    ? "info"
                    : "success",
                // D9 -- it said "Signed: {item}" and stopped, so
                // somebody who had just signed could not tell whether
                // the ceremony was finished or who had it now.
                // A1 -- naming the next seat is true and useless when
                // nobody is sitting in it. Every dual sign-off ended at
                // "now with the Accountable Executive" and stopped
                // there for good, and this toast was the last thing the
                // signer saw. Whoever just signed is exactly the person
                // who needs telling.
                //
                // Round 10 -- and it must tell them something TRUE. The
                // product knows whether it had anyone to notify, not
                // whether the seat is filled, so this is informational
                // rather than a warning: the ceremony is not stuck.
                // Round 14 D5 -- an owner seat nobody fills is not a
                // person who has not signed in. The toast said "has not
                // opened MARSAD yet" of an owner who does not exist,
                // and the tolerance could never complete.
                message: !action.next_role
                  ? t("myWork.sign.signedComplete", { title: itemTitle(t, item) })
                  : action.next_owner_missing
                    ? t("myWork.sign.signedOwnerMissing", {
                        title: itemTitle(t, item),
                        role: roleLabel(action.next_role),
                      })
                    : action.next_role_unnotified
                      ? t("myWork.sign.signedRoleUnnotified", {
                          title: itemTitle(t, item),
                          role: roleLabel(action.next_role),
                        })
                      : /* Round 18 N11 -- the person, where the
                           server knows one: the next screen named
                           him while this said "the Business owner". */
                        action.next_holder_name
                        ? t("myWork.sign.signedNowWithNamed", {
                            title: itemTitle(t, item),
                            name: action.next_holder_name,
                            role: roleLabel(action.next_role),
                          })
                        : t("myWork.sign.signedNowWith", {
                            title: itemTitle(t, item),
                            role: roleLabel(action.next_role),
                          }),
                // Not a warning: nothing has gone wrong, and a warning
                // tone on a ceremony that is proceeding normally is how
                // a reader learns to discount the product's warnings.
                //
                // Round 11 D4 -- and the mechanism rides underneath
                // rather than inside. The sentence above is what the
                // signer needs; how MARSAD comes to know who holds a
                // role is a second glance, for the reader who wants it.
                ...(action.next_owner_missing
                  ? { detail: t("myWork.sign.signedOwnerMissingDetail") }
                  : action.next_role_unnotified
                    ? { detail: t("myWork.sign.signedRoleUnnotifiedDetail") }
                    : {}),
              }),
            onError: (err) => {
              // ROUND 15 G1 -- a refusal that names a catalogue key is the
              // server's own reason (the environment switched signing
              // off, K2) and is shown as such; a bare 403 is still an
              // authority answer and reads "not yours". Either way the
              // inbox is re-read, so a panel over a leg this seat cannot
              // complete does not stay open with a live button.
              const coded =
                err instanceof ApiError && err.code ? refusalMessage(t, err) : null;
              const refused = refusalKey(err);
              toast.push({
                tone: "error",
                message: coded ?? (refused ? t(refused) : t("myWork.sign.failedToast")),
                ...(coded || refused ? {} : { detail: err.message }),
              });
              void qc.invalidateQueries({ queryKey: ["my-work"] });
            },
          },
        )
      }
    >
      {action.note_field && (
        <label className="block text-xs text-slate-600">
          {t("myWork.sign.comment")}
          <textarea
            dir="auto"
            data-testid={`work-sign-comment-${item.id}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
          />
        </label>
      )}
      {perform.isError && (
        <p
          data-testid={`work-action-error-${item.id}`}
          className="text-xs text-rose-700"
        >
          {perform.error instanceof ApiError && perform.error.code
            ? refusalMessage(t, perform.error)
            : refusalKey(perform.error)
              ? t(refusalKey(perform.error) as string)
              : perform.error.message}
        </p>
      )}
    </ReviewAndSign>
  );
}

/** One deadline band. Queue work carries deadlines too, so it is
 * grouped the same way -- an ordering that only applied to work
 * addressed to you would miss the case where none is. */
function BandSection({
  band,
  group,
  prefix = "",
  onPanel = () => {},
}: {
  band: DueBand;
  group: WorkItem[];
  prefix?: string;
  onPanel?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  if (group.length === 0) return null;
  return (
    <section className="space-y-2" data-testid={`my-work-${prefix}band-${band}`}>
      <h2
        className={`font-mono text-[10.5px] uppercase tracking-[0.18em] ${
          band === "overdue" ? "text-rag-red" : "text-slate-400"
        }`}
      >
        {t(`myWork.band.${band}`)} ({group.length})
      </h2>
      {/* B2 -- the reviewer's Thursday was 24 challenge rows in one
          undated band with no order and no grouping, so the only way to
          tell which belonged together was to open them.

          Grouping applies to the undated band alone: dated work is
          already ordered by the thing that orders it, and a second
          grouping on top would fight the deadline. */}
      {band === "undated" && group.some((w) => w.group_label) ? (
        <>
          <p className="text-[11px] text-slate-500">{t("myWork.band.groupedRule")}</p>
          {/* Round 14 G, eighth round -- "no review-by date" was the
              reviewer's complaint, and the product had a reason it did
              not print: nothing on a pack carries a deadline, and one
              invented here would drive real behaviour off a date
              nobody chose. Said, with the act that removes the
              repetition beside it. */}
          <p data-testid="my-work-undated-why" className="text-[11px] text-slate-500">
            {t("myWork.band.undatedWhy")}
          </p>
          {groupByLabel(group).map(([label, rows]) => (
            <div key={label ?? "__ungrouped"} className="space-y-1">
              {/* Round 10 D3 -- `dir="auto"` on the HEADING set the
                  whole block's direction from its first strong
                  character, so a Latin group name measured `ltr` and
                  sat at the left edge while the identical words in the
                  row beneath measured `rtl` and sat at the right: the
                  same words at two edges of one list.

                  `<bdi>` is the difference between isolating a NAME
                  and re-pointing its container. The heading flows with
                  the list; the name inside it still renders in its own
                  direction. */}
              <div className="flex flex-wrap items-baseline gap-2">
                <h3
                  data-testid={`my-work-group-${label ?? "other"}`}
                  className="text-[11px] font-medium text-slate-600"
                >
                  <bdi>{label ?? t("myWork.band.groupNone")}</bdi>
                </h3>
                {label ? (
                  <GroupNoObjection subject={label} rows={rows} onPanel={onPanel} />
                ) : null}
              </div>
              <ul className="space-y-2">
                {rows.map((w) => (
                  <WorkRow key={w.id} item={w} onPanel={onPanel} />
                ))}
              </ul>
            </div>
          ))}
        </>
      ) : (
        <ul className="space-y-2">
          {group.map((w) => (
            <WorkRow key={w.id} item={w} onPanel={onPanel} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Rows by what they belong to, preserving the server's order (which is
 * the pack's tier) inside each group. Rows belonging to nothing sort
 * last: a supplier pack has no business service, and that is a real
 * answer rather than a gap.
 */
function groupByLabel(items: WorkItem[]): [string | null, WorkItem[]][] {
  const by = new Map<string | null, WorkItem[]>();
  for (const w of items) {
    const k = w.group_label ?? null;
    const list = by.get(k);
    if (list) list.push(w);
    else by.set(k, [w]);
  }
  return [...by.entries()].sort((a, b) => {
    if (a[0] === null) return 1;
    if (b[0] === null) return -1;
    return a[0].localeCompare(b[0]);
  });
}

export function MyWorkView() {
  const { t, i18n } = useTranslation();
  // B1 -- while a signer is reading a decision the list holds still.
  const [panelsOpen, setPanelsOpen] = useState(0);
  const onPanel = (open: boolean) =>
    setPanelsOpen((n) => Math.max(0, n + (open ? 1 : -1)));
  const work = useMyWork(panelsOpen > 0);
  const items = work.data?.items ?? [];
  const waiting = work.data?.waiting_on ?? [];
  // "queue" items are reachable but not addressed to the viewer.
  const mine = items.filter((w) => w.ownership !== "queue");
  const queued = items.filter((w) => w.ownership === "queue");
  const today = new Date().toISOString().slice(0, 10);
  const bands = groupByDue(mine, today);
  const queueBands = groupByDue(queued, today);
  // Collapsed is right when the queue sits UNDER your own work. When
  // nothing is assigned to you it is not a footnote, it is the page --
  // and hiding a person's entire workload behind a "+" is the failure
  // this whole pass exists to remove.
  const [showQueue, setShowQueue] = useState<boolean | null>(null);
  const queueOpen = showQueue ?? mine.length === 0;

  return (
    <div data-testid="my-work-view" className="mx-auto max-w-3xl space-y-6 px-4 py-6">
      <header>
        <h1 className="font-display text-2xl text-slate-900">{t("myWork.heading")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("myWork.subtitle")}</p>
      </header>

      <Explainer termKey="positions" className="max-w-3xl" />

      {work.isLoading && (
        <p className="text-sm text-slate-400">{t("myWork.loading")}</p>
      )}
      {work.isError && <p className="text-sm text-rose-700">{t("myWork.error")}</p>}
      {!work.isLoading && !work.isError && items.length === 0 && (
        <div data-testid="my-work-empty" className="space-y-1 text-sm text-slate-500">
          <p>{work.data?.reads_only ? t("myWork.readsOnly") : t("myWork.empty")}</p>
        </div>
      )}

      {/* B4 -- "nothing waiting on you" alone reads as "you have
          nothing in flight". Say where your own work went.

          Round 10 F2 -- and say it whether or not the inbox is empty.
          This was rendered ONLY in the empty branch, and the person it
          was written for -- a preparer who has just submitted a
          tolerance and wants to know where it went -- almost always
          has something else in the list, so he never saw it. Sixth
          round on a sentence the server had been composing correctly
          the whole time. */}
      {!work.isLoading && !work.isError && waiting.length > 0 && (
        <section data-testid="my-work-waiting" className="space-y-1">
          <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
            {t("myWork.waitingHeading")}
          </h2>
          {waiting.map((w) => (
            <p
              key={`${w.title}-${w.awaiting_role}`}
              data-testid={
                w.coming_to_you ? "my-work-coming-to-you" : "my-work-waiting-on"
              }
              className="text-sm text-slate-500"
            >
              {/* Round 11 F4 -- the seat AND the person, where the
                  bank's own record names exactly one holder of it.
                  D2 fixed the mechanism and left the substance: the
                  line said "is with the CRO", and the reader's only
                  question is who to chase. Where nobody holds the
                  seat, or several do, it stays the seat -- naming one
                  of three sends them to the wrong person. */}
              {t(
                w.coming_to_you
                  ? "myWork.comingToYou"
                  : w.since
                    ? "myWork.waitingOnSince"
                    : "myWork.waitingOn",
                {
                  title: w.title_key ? t(w.title_key, w.title_params ?? {}) : w.title,
                  role: w.awaiting_name
                    ? t("myWork.waitingOnWho", {
                        who: w.awaiting_name,
                        role: roleBadge(seatOf(w.awaiting_role)),
                      })
                    : roleBadge(w.awaiting_role),
                  since: w.since ? formatDate(w.since, i18n.language) : "",
                },
              )}
              {w.owner_missing ? (
                <span
                  data-testid="my-work-waiting-owner-missing"
                  className="ms-1 text-amber-800"
                >
                  {t("myWork.waitingOnOwnerMissing")}
                </span>
              ) : (
                (w.awaiting_holders ?? 0) === 0 && (
                  <span
                    data-testid="my-work-waiting-unheld"
                    className="ms-1 text-amber-800"
                  >
                    {t("myWork.waitingOnUnheld")}
                  </span>
                )
              )}
            </p>
          ))}
        </section>
      )}

      {/* Yours first, and within that BY DEADLINE (C4). An inbox
          grouped by entity type answers "what kind of thing is this?";
          the question every morning is "what is due?". */}
      {BAND_ORDER.map((band) => (
        <BandSection key={band} band={band} group={bands[band]} onPanel={onPanel} />
      ))}

      {!work.isLoading && !work.isError && mine.length === 0 && queued.length > 0 && (
        <p data-testid="my-work-nothing-yours" className="text-sm text-slate-500">
          {t("myWork.nothingYours")}
        </p>
      )}

      {queued.length > 0 && (
        <section className="space-y-2" data-testid="my-work-queue">
          <button
            type="button"
            onClick={() => setShowQueue(!queueOpen)}
            aria-expanded={queueOpen}
            data-testid="my-work-queue-toggle"
            className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400 hover:text-slate-600"
          >
            {t("myWork.queueGroup", { count: queued.length })}{" "}
            {queueOpen ? "\u2212" : "+"}
          </button>
          {queueOpen &&
            BAND_ORDER.map((band) => (
              <BandSection
                key={band}
                band={band}
                group={queueBands[band]}
                prefix="queue-"
                onPanel={onPanel}
              />
            ))}
        </section>
      )}
    </div>
  );
}
